/**
 * @import CTypes from "../types"
 */

import { create } from 'zustand';
/** @type {CTypes.ShipTiers} */
import ShipTiers from "../assets/json/shiptiers.json";
/** @type {CTypes.ShipFiltersFile}*/
import FilterDefinitions from "../assets/json/shipFilters.json";
import SearchAliases from "../assets/json/searchAliases.json";
import { getShipInfo, getShipJSON, getShipRarityCSSClass, isShipKai, getShipImage } from './ShipHelpers';
import { DEFAULT_IMAGE } from './Helpers';

export class Ship {
  /** @type {string} */
  name;
  /** @type {CTypes.ShipInfo | undefined} */
  shipInfo;
  /** @type {CTypes.ShipJson | undefined} */
  shipJson;

  shipImage;
  shipBanner;

  constructor(name) {
    this.name = name;
  }

  /**
   *
   * @return {string}
   */
  getDisplayName() {
    return this.getShipJson().Name;
  }

  /**
   * @return {CTypes.ShipInfo}
   */
  getShipInfo() {
    if (this.shipInfo === undefined) {
      this.shipInfo = getShipInfo(this.name);
    }
    return this.shipInfo;
  }

  /**
   * @returns {CTypes.ShipJson}
   */
  getShipJson() {
    if (this.shipJson === undefined) {
      this.shipJson = getShipJSON(this.name);
    }
    return this.shipJson;
  }

  /**
   * @returns {boolean}
   */
  isKai() {
    return isShipKai(this.getShipInfo(), this.getShipJson());
  }

  /**
   * @returns {string}
   */
  getRarityClass() {
    return getShipRarityCSSClass(this.getShipJson(), this.isKai());
  }

  getImage() {
    if (this.shipImage === undefined) {
      this.shipImage = getShipImage(this.name, this.isKai(), false);
    }
    return this.shipImage;
  }

  getBannerImage() {
    if (this.shipBanner === undefined) {
      this.shipBanner = getShipImage(this.name, this.isKai(), true);
    }
    return this.shipBanner;
  }

  /**
   * @return {string}
   */
  getLinkName() {
    return this.getShipJson().Name.replace(/\s/g, "_").toLowerCase();
  }

  /**
   * @return {string}
   */
  getWikiLink() {
    return "https://azurlane.koumakan.jp/wiki/" + this.getShipJson().Name.replace(/\s/g, "_");
  }

  /**
   * @return {string}
   */
  getUppercaseName() {
    return this.name.toUpperCase().replaceAll('Μ', 'μ');
  }
}

export const useThemeStore = create((set, get) => ({
  theme: "light", // light = false; dark = true

  getThemeFromLocalStorage: () => {
    set({
      theme: localStorage.getItem('theme') === 'true' ? 'dark' : 'light'
    })
  },

  toggleTheme: () => {
    set({
      theme: get().isLightTheme() ? "dark" : "light"
    })
    localStorage.setItem(
      "theme",
      get().isLightTheme() ? "false" : "true"
    );
  },

  isLightTheme: () => {
    return get().theme === "light";
  },

  isDarkTheme: () => {
    return get().theme === "dark";
  }
}))

export const useScrollStore = create((set) => ({
  scrollBarWidth: 0,
  scrollPosition: 0,

  updateScrollBarWidth: () => {
    const root = document.getElementById("root");

    let width = window.innerWidth - root.offsetWidth;
  
    set({scrollBarWidth: width});
  },

  setScrollPosition: (position) => {
    set({ scrollPosition: position });
  }
}))

// this is a cache for images
export const useImageStore = create((set, get) => ({
  images: {},

  /**
   * @param key {string} the image url
   * @param setFn {(value: string) => void} the callback to update the image
   */
  getImage(key, setFn) {
    const state = get();

    if (state[key] !== undefined) {
      setFn(state[key]);
    } else {
      setFn(DEFAULT_IMAGE);

      fetch(key)
        .then(res => res.blob())
        .then(blob => {
          const newState = get();
          newState[key] = URL.createObjectURL(blob);
          set(newState);
          setFn(newState[key]);
        });
    }
  },

  removeImage(key) {
    const state = get();
    delete state[key];
    set(state);
  },

  resetCache() {
    set({});
  }
}))

export const useShipStore = create((set, get) => ({
  /** @type {CTypes.ShipTiers} */
  filteredShipTiers: ShipTiers,
  /** @type {string} */
  search: "",
  /** @type {string[]} */
  filters: [],
  /** @type {number} */
  activeTab: 0,
  /** @type {Record<string, Ship>} */
  ships: getShips(),

  /** @type {(tab: number) => void} */
  setActiveTab: (tab) => {
    set({ activeTab: tab });
  },

  /** @type {(search: string) => void} */
  setSearch: (search) => {
    set({
      search: search,
    });

    get().updateFilteredShips();
  },

  /** @type {() => void} */
  clearSearch: () => {
    set({
      search: "",
    });

    get().updateFilteredShips();
  },

  /** @type {(filter: string, active: boolean) => void} */
  setFilter: (filter, active) => {
    let filters = get().filters.slice();
    if (active) {
      if (FilterDefinitions.multi_filter) {
        filters.push(filter);
        for (const group of FilterDefinitions.exclusive_groups) {
          if (group.includes(filter)) {
            filters = filters.filter((f) => !group.includes(f));
            filters.push(filter);
            break;
          }
        }
      } else {
        filters = [filter];
      }
    } else {
      filters = filters.filter((f) => f !== filter);
    }

    set({
      filters: filters,
    });

    get().updateFilteredShips();
  },

  /** @type {() => void} */
  clearFilters: () => {
    set({
      filters: [],
    });

    get().updateFilteredShips();
  },

  updateFilteredShips: () => {
    const state = get();

    set({
      filteredShipTiers: filterShipTiers(ShipTiers, state.search, state.filters),
    });
  },

  /** @type {(hullType: string) => CTypes.ShipFilterDefinition[]} */
  getFiltersAffectingHullType: (hullType) => {
    /** @type {CTypes.ShipFilterDefinition[]} */
    const filters = [];

    for (const filterDefinition of FilterDefinitions.filters) {
      // we run a filter with only the hull type to see if it affects it
      /** @type {string[]} */
      const filteredShips = filterShipTiers(
        {
          [hullType]: ShipTiers[hullType]
        },
        "",
        [filterDefinition.label]
      )[hullType].flat();

      if (filteredShips.length !== 0 && filteredShips.length !== ShipTiers[hullType].flat().length) {
        filters.push(filterDefinition);
      }
    }

    return filters;
  },

  tabHasShips: (tab) => {
    return get().filteredShipTiers[Object.keys(ShipTiers)[tab]].flat().length > 0;
  }
}));

/**
 * @returns {Record<string, Ship>}
 */
function getShips() {
  const ships = Object.values(ShipTiers).flat(2).map((shipName) => [
    shipName,
    new Ship(shipName)
  ]);

  return Object.fromEntries(ships);
}

/**
 *
 * @param str {string}
 * @return {string}
 */
function normalizeString(str) {
  return str.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/**
 *
 * @param shipTiers {Record<string, CTypes.ShipTier>}
 * @param search {string}
 * @param filters {string[]}
 * @return {Record<string, CTypes.ShipTier>}
 */
function filterShipTiers(shipTiers, search, filters) {
  /** @type {Record<string, CTypes.ShipTier>} */
  const filteredShipTiers = {};
  /** @type {CTypes.ShipFilterDefinition[]} */
  const filterDefinitions = FilterDefinitions.filters.filter(x => filters.includes(x.label));
  const normalizedSearch = prepareSearch(search);

  for (const [hull, hullTiers] of Object.entries(shipTiers)) {
    filteredShipTiers[hull] = hullTiers.map(tier => {
      return tier.filter(shipName => shouldShowShip(shipName, normalizedSearch, filterDefinitions));
    });
  }

  return filteredShipTiers;
}

/**
 *
 * @param shipName {string}
 * @param search {string[]}
 * @param filters {CTypes.ShipFilterDefinition[]}
 * @return {boolean}
 */
function shouldShowShip(shipName, search, filters) {
  const shipInfo = getShipInfo(shipName);
  const shipRoles = shipInfo.roles.flat();
  const shipGhostRoles = shipInfo.ghost_roles?.flat() ?? [];
  let shouldShow = true;

  if (search.length > 0) {
    const normalizedShipName = prepareNameForSearch(shipName);

    // for every search term, we need to find one part or alias of a part of the ship name
    shouldShow &= search.every(searchTerm => normalizedShipName.some(name => name.some(alias => alias.includes(searchTerm))));
  }

  for (const filter of filters) {
    if (!shouldShow) {
      break;
    }

    // look if the ship has roles that are included in the filter
    let included = false;

    for (const role of filter.include.roles) {
      if (shipRoles.includes(role)) {
        included = true;
        break;
      }
    }

    for (const role of filter.include.ghost_roles) {
      if (shipGhostRoles.includes(role)) {
        included = true;
        break;
      }
    }

    // look if the ship has roles that are excluded in the filter
    let excluded = false;

    for (const role of filter.exclude.roles) {
      if (shipRoles.includes(role)) {
        excluded = true;
        break;
      }
    }

    for (const role of filter.exclude.ghost_roles) {
      if (shipGhostRoles.includes(role)) {
        excluded = true
        break;
      }
    }

    // if the filter has no include roles, then the ship should be shown if it doesn't have any excluded roles
    if (filter.include.roles.length === 0 && filter.include.ghost_roles.length === 0) {
      shouldShow &= !excluded;
    }
    // if the filter has no exclude roles, then the ship should be shown if it has any included roles
    else if (filter.exclude.roles.length === 0 && filter.exclude.ghost_roles.length === 0) {
      shouldShow &= included;
    }
    // if the filter has both include and exclude roles, then the ship should be shown if it has any included roles and no excluded roles
    else {
      shouldShow &= included && !excluded;
    }
  }

  return shouldShow;
}

/**
 *
 * @param search
 * @return {string[]}
 */
function prepareSearch(search) {
	const normalizedSearch = normalizeString(search);

  return normalizedSearch.split(" ");
}

/**
 *
 * @param name {string}
 * @return {string[][]}
 */
function prepareNameForSearch(name) {
  const normalizedName = normalizeString(name);

  const splitName = normalizedName.split(" ");
  return splitName.map(term => getNameAliases(term));
}

/**
 *
 * @param name {string}
 * @return {string[]}
 */
function getNameAliases(name) {
  return [...(SearchAliases[name] ?? []), name];
}